import Box from "@mui/material/Box";
import { arrayTimes } from "common/utilities/core/array";
import useScroll from "common/utilities/hooks/useScroll/useScroll";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import theme, { ThemeColors, ThemeVars } from "core/utilities/theme";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { TABLE_ACTIONS_HEIGHT } from "sequenceAnalyser/components/TableActions/TableActionsHeader/TableActionsHeader.style";
import { RESIDUE_PIXEL_WIDTH, RULER_PIXEL_HEIGHT } from "sequenceAnalyser/utilities/screen/screen";

import classes from "./SequenceTableRuler.style";

interface ComponentProps {
  totalLength: number;
  zoomLevel: number;
  sidebarPixelWidth: number;
  svgOnlyWithYPosition?: number;
}

function SequenceTableRuler({ totalLength, zoomLevel, sidebarPixelWidth, svgOnlyWithYPosition }: ComponentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const zoomLevelPercentage = (zoomLevel || 100) / 100;
  const showAppHeaderEnv = useSelector((state: ApplicationState) => state.ui.showAppHeaderEnv);
  const scroll = useScroll();

  /**
   * The element is positioned absolute so it can be scrolled horizontally.
   * We want it to be fixed on vertical scroll so we have to adjust its 'top' and 'left' value.
   */
  const handleScroll = useCallback(
    (axis?: "x" | "y") => {
      let defaultTop = ThemeVars.headerMenuHeight;
      if (showAppHeaderEnv) defaultTop += ThemeVars.headerEnvHeight;
      defaultTop += TABLE_ACTIONS_HEIGHT;

      if (ref && ref.current) {
        if (axis === "y") {
          ref.current.style.top = `${defaultTop}px`;
          ref.current.style.left = `-${window.scrollX}px`;
          ref.current.style.position = "fixed";
        } else if (axis === "x") {
          const top = window.scrollY + defaultTop;
          ref.current.style.top = `${top}px`;
          ref.current.style.left = "auto";
          ref.current.style.position = "absolute";
        } else if (ref.current.style.top.length === 0) {
          const top = window.scrollY + defaultTop;
          ref.current.style.top = `${top}px`;
        }
      }
    },
    [showAppHeaderEnv],
  );

  useEffect(() => {
    handleScroll(scroll.scrollingAxis);
  }, [handleScroll, scroll.scrollingAxis]);

  const rulerWidth = RESIDUE_PIXEL_WIDTH * totalLength * zoomLevelPercentage;
  const sidebarRatioWidth = sidebarPixelWidth * zoomLevelPercentage;

  const svgContent = useMemo(() => {
    return (
      <svg
        width={rulerWidth}
        height={RULER_PIXEL_HEIGHT}
        xmlns="http://www.w3.org/2000/svg"
        y={svgOnlyWithYPosition}
        style={{ userSelect: "none", background: ThemeColors.backgroundColor }}
      >
        <g>
          {/* RENDER ALL TEXT IN ONE BLOCK */}
          {arrayTimes(totalLength).map((_t, i) => {
            const columnPosition = i + 1;
            const columnWidth = RESIDUE_PIXEL_WIDTH * zoomLevelPercentage;
            const x = (columnPosition - 1) * columnWidth;
            const xCenter = x + columnWidth / 2;

            if (columnPosition === 1 || columnPosition % 10 === 0) {
              return (
                <text
                  key={`t-${i}`}
                  x={xCenter}
                  y={16}
                  textAnchor="middle"
                  strokeWidth="1px"
                  fill={theme.palette.text.primary}
                  fontSize="11"
                  fontFamily={theme.typography.fontFamily}
                >
                  {columnPosition}
                </text>
              );
            }
            return null;
          })}

          {/* COMBINE LINES INTO A SINGLE PATH */}
          <path
            d={arrayTimes(totalLength)
              .map((_, i) => {
                const columnPosition = i + 1;
                const columnWidth = RESIDUE_PIXEL_WIDTH * zoomLevelPercentage;
                const x = (columnPosition - 1) * columnWidth + columnWidth / 2;

                const height = columnPosition === 1 || columnPosition % 5 === 0 ? 6 : 3;
                return `M${x},0 L${x},${height}`;
              })
              .join(" ")}
            stroke="#000"
            strokeWidth="1"
            shapeRendering="crispEdges"
            fill="none"
          />

          {/* COMBINE RECTS IF YOU NEED INTERACTIVITY */}
          {arrayTimes(totalLength).map((_, i) => {
            const columnPosition = i + 1;
            const columnWidth = RESIDUE_PIXEL_WIDTH * zoomLevelPercentage;
            const columnHeight = RULER_PIXEL_HEIGHT;
            const x = (columnPosition - 1) * columnWidth;

            return (
              <rect
                key={`r-${i}`}
                x={x + 1}
                y={1}
                width={columnWidth - 4}
                height={columnHeight - 4}
                fillOpacity="0"
                strokeWidth={2}
                onMouseEnter={(e) => e.currentTarget.setAttribute("stroke", theme.palette.text.primary)}
                onMouseLeave={(e) => e.currentTarget.setAttribute("stroke", "")}
              >
                <title>{columnPosition}</title>
              </rect>
            );
          })}
        </g>
      </svg>
    );
  }, [rulerWidth, svgOnlyWithYPosition, totalLength, zoomLevelPercentage]);

  if (svgOnlyWithYPosition) {
    return svgContent;
  }

  return (
    <Box
      id="sequenceTableRuler"
      ref={ref}
      sx={{
        ...classes.root,
        width: `${rulerWidth}px`,
      }}
    >
      <Box
        sx={{
          ...classes.sidebar,
          width: `${sidebarRatioWidth}px`,
        }}
      >
        <Box sx={{ width: `${sidebarRatioWidth}px` }} />
      </Box>
      <Box
        sx={{
          ...classes.ruler,
        }}
      >
        {svgContent}
      </Box>
    </Box>
  );
}

export default memo(SequenceTableRuler);
